
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  BrowserAuthError
} from "@azure/msal-browser";
import { loginRequest, IdTokenClaims } from "../authConfig";
import { msalInstance } from "../index";
import { format, isValid, parseISO } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getCurrentUserId } from "./CurrentUser";

// ------------------------------
// API Helper
// ------------------------------

/**
 * Simple in-flight guard to prevent multiple concurrent token requests
 * (common in React when many components call APIs at once)
 */
let tokenRequestInFlight: Promise<any> | null = null;

export async function OPPApiHelper(apiurl: string, apioptions: any) {
  apiurl = (process.env.REACT_APP_API_HOST_URL || "") + apiurl;

  // Prefer active account, fallback to first cached account
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const all = msalInstance.getAllAccounts();
    if (all && all.length > 0) {
      account = all[0];
      msalInstance.setActiveAccount(account);
    }
  }

  if (!account) {
    // No account means the user is not signed-in or cache got cleared
    clearLocalStorageExceptFilterAndSort();
    sessionStorage.clear();
    window.location.href = window.location.origin + "/SessionTimeout";
    return "";
  }

  const authResult = await acquireToken(account);

  // If we triggered redirect fallback, the browser will navigate away.
  // Returning prevents any further work.
  if (!authResult) {
    return "";
  }

  const headers = createHeaders(authResult.accessToken, apiurl);
  apioptions.headers = headers;

  return fetchAndHandleResponse(apiurl, apioptions);
}

async function acquireToken(account: any) {
    const apiScope = process.env.REACT_APP_API_SCOPE;
    if (!apiScope) {
      throw new Error("API scope is not defined. Please set REACT_APP_API_SCOPE in the environment.");
    }

    const tokenRequest = {
      ...loginRequest,
      account,
      scopes: [apiScope],
      // IMPORTANT:
      // Don't set forceRefresh based on ID token expiry. Let MSAL manage token freshness.
      // Forcing refresh can cause frequent silent iframe renewals -> timeouts in some browsers/environments.
      forceRefresh: false,
      cacheLookupPolicy: CacheLookupPolicy.Default
    };

  // In-flight guard to avoid multiple concurrent silent calls
  if (!tokenRequestInFlight) {
    tokenRequestInFlight = msalInstance
      .acquireTokenSilent(tokenRequest)
      .finally(() => {
        tokenRequestInFlight = null;
      });
  }

  try {
    return await tokenRequestInFlight;
  } catch (e: any) {
    /**
     * Silent token acquisition can fail due to:
     * - cookie restrictions / iframe limitations
     * - session not available
     * - conditional access changes
     * - intermittent network issues
     *
     * In such cases, MSAL guidance is to fall back to an interactive method.
     */
    const errorCode = e?.errorCode || e?.code;

    const isTimeout =
      errorCode === "timed_out" ||
      errorCode === "monitor_window_timeout" ||
      errorCode === "token_renewal_error";

    if (e instanceof InteractionRequiredAuthError || isTimeout) {
      // Most reliable fallback for enterprise browsers is redirect:
      // (popups can be blocked by policies)
      // Use prompt: "select_account" to avoid "We couldn't sign you in" error
      // when Azure AD SSO session cookie has expired after inactivity.
      msalInstance.acquireTokenRedirect({
        ...tokenRequest,
        prompt: "select_account"
      });
      return null;
    }

    throw e;
  }
}

function createHeaders(accessToken: string, apiurl: string) {
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken}`);

  if (
    !apiurl.includes("PORDetails/CreatePORItem") &&
    !apiurl.includes("PORDetails/SaveAttachment") &&
    !apiurl.includes("IntakeManagement/CreateIntakePORItem") &&
    !apiurl.includes("MetadataManagement/SaveMetadata") 
  ) {
    headers.append("Content-Type", "application/json");
  }

  return headers;
}

async function fetchAndHandleResponse(apiurl: string, apioptions: any) {
  try {
    const response = await fetch(apiurl, apioptions);

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorData = await response.json();
        // Structured validation responses (e.g. OLS Offer CreateOLSOfferResponse) use PascalCase
        // "Errors": [{ Field, Message }] because the API disables camelCase naming.
        const structuredErrors = Array.isArray(errorData?.Errors)
          ? Array.from(new Set(errorData.Errors.map((e: any) => (typeof e === "string" ? e : e?.Message)).filter(Boolean))).join(" ")
          : "";
        errorMessage = errorData.message || errorData.Message || structuredErrors || JSON.stringify(errorData.errors) || errorMessage;
      } catch {
        // Response body is empty or not valid JSON — use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return response.status === 204 ? null : await response.json();
  } catch (error: any) {
    // Network-level error (e.g., VPN not connected)
    if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
      throw new Error(
        "Please ensure you are connected to MSFT-AzVPN-Manual to access OPP. If you’re still having trouble, contact us at pecoplatformteams@microsoft.com."
      );
    } else {
      throw error;
    }
  }
}

// ------------------------------
// Utilities (your existing code)
// ------------------------------

// for to display datetime to time ago
export const calculateTimeAgo = (inputDate: any) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const currentDate = dayjs();
  const inputDateToLocalDate = dayjs.utc(inputDate).tz(dayjs.tz.guess());
  const diffMinutes = currentDate.diff(dayjs(inputDateToLocalDate), "minute");

  if (diffMinutes < 1) {
    return "just now";
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  } else if (diffMinutes < 1440) {
    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  } else if (diffMinutes < 10080) {
    // Less than a week (7 days)
    const diffDays = Math.floor(diffMinutes / 1440);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } else {
    return inputDateToLocalDate.format("D MMM YYYY [at] HH:mm");
  }
};

// NOTE: You can keep isValidToken if you still use it elsewhere,
// but DO NOT use it to drive MSAL forceRefresh.
//Check if idToken has expired
function isValidToken(account: any) {
  const idTokenClaims = account.idTokenClaims as IdTokenClaims;
  const expirationDate = new Date(idTokenClaims["exp"] * 1000 - 1 * 60000);
  return expirationDate > new Date();
}

export const removeWhiteSpace = (str: string) => {
  if (str === null || str === undefined) return str;
  if (typeof str !== "string") return str;
  if (str.trim() === "") return "";
  return str.replace(/\s+/g, " ").trim();
};

export const isEmpty = (value: any) => {
  let empty = false;

  if (value === undefined || value === null || value === "") {
    empty = true;
  } else if (typeof value === "string") {
    const htmlContent = value.replace(/<[^>]+>/g, "").trim();
    const sanitizedContent = htmlContent.replace(/&nbsp;/g, "").trim();
    if (value.trim() === "" || sanitizedContent === "") {
      empty = true;
    }
  } else if (Array.isArray(value) && value.length === 0) {
    empty = true;
  } else if (value instanceof Date && isNaN(value.getTime())) {
    empty = true;
  } else if (typeof value === "object" && value !== null) {
    if (value instanceof Date) {
      empty = false;
    } else if (Object.keys(value).length === 0) {
      empty = true;
    } else if (Object.values(value).every((item) => isEmpty(item))) {
      empty = true;
    }
  } else if (value === 0) {
    empty = true;
  }

  return empty;
};

export const deepEqual = (obj1: any, obj2: any) => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);

  if (obj1Keys.length !== obj2Keys.length) return false;

  for (const key of obj1Keys) {
    if (!Object.prototype.hasOwnProperty.call(obj2, key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

dayjs.extend(utc);
dayjs.extend(timezone);

export const parseDateValue = (value: string | Date): Date => {
  if (typeof value !== "string") return value;
  // Date-only values are calendar dates, not UTC timestamps.
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISO(value) : new Date(value);
};

// Format Date: MM/dd/yyyy
export const onFormatDate = (_date?: any): string => {
  const dateObj = typeof _date === "string" ? parseDateValue(_date) : _date;
  if (!isValid(dateObj)) return "";
  return format(dateObj, "MM/dd/yyyy");
};

// Format DateTime: MM/DD/YYYY HH:mm (local timezone)
export const onFormatDateTime = (_datetime?: any): string => {
  try {
    const dateObj = dayjs.utc(_datetime);
    if (!dateObj.isValid()) return "";
    const localDateTime = dateObj.tz(dayjs.tz.guess());
    return localDateTime.format("MM/DD/YYYY HH:mm");
  } catch {
    return "";
  }
};

export const convertToCsv = (jsonData: any, fileName: any) => {
  const csvRows: string[] = [];

  const headers = Array.from(new Set(jsonData.flatMap((obj: any) => Object.keys(obj))));
  csvRows.push(headers.join(","));

  for (const row of jsonData) {
    const values = headers.map((header: any) => {
      let value = row[header];
      if (value === undefined || value === null) value = "";
      const escaped = String(value ?? "").replace(/"/g, '""');

      if (escaped.includes(",") || escaped.includes("\n") || escaped.includes('"')) {
        return `"${escaped}"`;
      }
      return escaped;
    });

    csvRows.push(values.join(","));
  }

  const csvData = "\uFEFF" + csvRows.join("\n");
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

//store key and value in localstorage
export function storeDataInLocalStorage(key: any, value: any) {
  localStorage.setItem(key, JSON.stringify(value));
}

//retrieve value by key from local storage
export function getDataFromLocalStorage(key: any) {
  const storedData = localStorage.getItem(key);
  if (storedData) return JSON.parse(storedData);
  return null;
}

// Function to clear local storage except keys containing "filter" and "sort"
export function clearLocalStorageExceptFilterAndSort() {
  let i = 0;
  while (i < localStorage.length) {
    const key = localStorage.key(i);

    if (key !== null && !key.toLowerCase().includes("filter") && !key.toLowerCase().includes("sort")) {
      localStorage.removeItem(key);
    } else {
      i++;
    }
  }
}

// Fetch saved filter preference from server
/** Error-aware opt-in reads. Unlike legacy helpers, failed/malformed reads never look absent. */
export async function readUserPreferenceStrict(filterKey: string): Promise<unknown | null> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("Sign in before loading saved views.");
  const result = await OPPApiHelper(
    `UserFilterPreference?userId=${encodeURIComponent(userId)}&filterKey=${encodeURIComponent(filterKey)}`,
    { method: "GET" }
  );
  if (result === null) return null;
  if (!result || typeof result.FilterValues !== "string") throw new Error("Invalid preference response.");
  const value: unknown = JSON.parse(result.FilterValues);
  if (value === null) throw new Error("Invalid stored preference: null is not a saved default.");
  return value;
}

export async function listUserPreferencesStrict(prefix: string): Promise<{ filterKey: string; values: unknown }[]> {
  if (!getCurrentUserId()) throw new Error("Sign in before loading saved views.");
  const result = await OPPApiHelper(`UserFilterPreference/list?keyPrefix=${encodeURIComponent(prefix)}`, { method: "GET" });
  if (!Array.isArray(result)) throw new Error("Invalid saved views response.");
  return result.map((row: { FilterKey: string; FilterValues: string }) => {
    if (typeof row.FilterKey !== "string" || !row.FilterKey.startsWith(prefix) || typeof row.FilterValues !== "string") {
      throw new Error("Invalid saved view response.");
    }
    return { filterKey: row.FilterKey, values: JSON.parse(row.FilterValues) as unknown };
  });
}

export async function saveUserPreferenceStrict(filterKey: string, values: unknown): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("Sign in before saving views.");
  const result = await OPPApiHelper("UserFilterPreference", {
    method: "POST",
    body: JSON.stringify({ UserId: userId, FilterKey: filterKey, FilterValues: JSON.stringify(values) })
  });
  if (!result || result.success !== true) throw new Error("The preference was not saved. Please sign in and retry.");
}

export async function getUserFilterPreference(filterKey: string): Promise<any> {
  const userId = getCurrentUserId();
  if (!userId || !filterKey) return null;
  try {
    const requestOptions = { method: "GET" };
    const result = await OPPApiHelper(
      `UserFilterPreference?userId=${encodeURIComponent(userId)}&filterKey=${encodeURIComponent(filterKey)}`,
      requestOptions
    );
    if (result && result.FilterValues) {
      return JSON.parse(result.FilterValues);
    }
    return null;
  } catch {
    return null;
  }
}

// Save filter preference to server
export async function saveUserFilterPreference(filterKey: string, filterValues: any): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId || !filterKey) return false;
  try {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({
        UserId: userId,
        FilterKey: filterKey,
        FilterValues: JSON.stringify(filterValues)
      })
    };
    await OPPApiHelper("UserFilterPreference", requestOptions);
    return true;
  } catch {
    return false;
  }
}

// List the caller's saved filter presets (FilterKeys beginning with keyPrefix)
export async function listUserFilterPresets(keyPrefix: string): Promise<{ filterKey: string; values: Record<string, unknown> }[]> {
  const userId = getCurrentUserId();
  if (!userId || !keyPrefix) return [];
  try {
    const result = await OPPApiHelper(
      `UserFilterPreference/list?keyPrefix=${encodeURIComponent(keyPrefix)}`,
      { method: "GET" }
    );
    if (!Array.isArray(result)) return [];
    return result.map((row: any) => ({
      filterKey: row.FilterKey,
      values: row.FilterValues ? JSON.parse(row.FilterValues) : {}
    }));
  } catch {
    return [];
  }
}

// Delete one of the caller's filter preferences
export async function deleteUserFilterPreference(filterKey: string): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId || !filterKey) return false;
  try {
    const result = await OPPApiHelper(`UserFilterPreference?filterKey=${encodeURIComponent(filterKey)}`, { method: "DELETE" });
    // OPPApiHelper resolves to "" when no token was acquired (redirect fallback); the request never reached the API.
    return result !== "";
  } catch {
    return false;
  }
}

// Numeric value validation
export function validateNumericValue(value: string): string {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";

  if (isNaN(Number(trimmedValue))) return "Please enter a valid number.";
  if (parseFloat(trimmedValue) < 0) return "Please enter a valid number.";

  if (trimmedValue.includes(".")) {
    const decimals = trimmedValue.split(".")[1];
    if (decimals && decimals.length > 2) {
      return "Only two decimal places are allowed. Please round your value.";
    }
  }

  return "";
}

export const staticLevelDiscountCodes = [
  { text: "Waterfall", value: "W4" },
  { text: "Flat", value: "WD" },
  { text: "Flat", value: "WB" },
  { text: "Flat", value: "W9" },
  { text: "Flat", value: "W7" }
];

export const getEffectiveMonth = (fileName: string): string => {
  const match = fileName.match(/_\d{4}(\d{2})\d{2}\./);
  if (match) {
    const monthIndex = parseInt(match[1], 10) - 1;
    const monthNames = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ];
    if (monthIndex >= 0 && monthIndex < 12) return monthNames[monthIndex];
  }
  return "-";
};

export const getFileVersion = (filePath: string): string => {
  const fileName = filePath.split("/").pop() || "";
  // Match {name}_{YYYYMMDD}{VVVVVV}.ext  e.g. Fxrule_20260301000002.xlsx
  const matchFull = fileName.match(/_\d{8}(\d{6})\./);
  if (matchFull) return String(parseInt(matchFull[1], 10));
  // Match {name}_{VVVVVV}.ext  e.g. ERPGrid_000002.xlsx
  const matchVersion = fileName.match(/_(\d{6})\./);
  if (matchVersion) return String(parseInt(matchVersion[1], 10));
  return "-";
};

const userPreferenceReads = new Map<string, Promise<string | null>>();

const readUserPreference = async (userId: string, preferenceKey: string): Promise<string | null> => {
  const result = await OPPApiHelper(
    `UserPreference?userId=${encodeURIComponent(userId)}&preferenceKey=${encodeURIComponent(preferenceKey)}`,
    { method: "GET" }
  );
  return result?.PreferenceValue || null;
};

// Fetch a user preference from server
export async function getUserPreference(
  preferenceKey: string,
  throwOnError = false
): Promise<string | null> {
  const userId = getCurrentUserId();
  if (!userId || !preferenceKey) return null;
  const key = JSON.stringify([userId, preferenceKey]);
  let pending = userPreferenceReads.get(key);
  if (!pending) {
    pending = readUserPreference(userId, preferenceKey);
    userPreferenceReads.set(key, pending);
    const clear = () => {
      if (userPreferenceReads.get(key) === pending) userPreferenceReads.delete(key);
    };
    void pending.then(clear, clear);
  }
  try {
    return await pending;
  } catch (error) {
    // Retain legacy handling for malformed preference responses.
    if (error instanceof SyntaxError) {
      return null;
    }
    if (throwOnError) {
      throw error;
    }
    return null;
  }
}

// Save a user preference to server
export async function saveUserPreference(preferenceKey: string, preferenceValue: string): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId || !preferenceKey) return false;
  try {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({
        UserId: userId,
        PreferenceKey: preferenceKey,
        PreferenceValue: preferenceValue
      })
    };
    await OPPApiHelper("UserPreference", requestOptions);
    return true;
  } catch {
    return false;
  }
}
